// -------------------------------------------------------------
// ENTERPRISE IDENTITY SERVICE FOR SATALA DERMATECH ESSENTIAL
// Core Singleton Engine for Visitor, User, Session & Device Management
// -------------------------------------------------------------
import { logAuditEvent } from '@/utils/AuditLogManager';

const VISITOR_JOURNEY_KEY = 'satala_visitor_journeys';
const ACTIVE_SESSIONS_KEY = 'satala_active_sessions';

// -------------------------------------------------------------
// INITIAL MOCK ACTIVE SESSIONS (HIERARCHICAL: User -> Devices)
// -------------------------------------------------------------
export const INITIAL_USER_SESSIONS = [];

export const INITIAL_VISITOR_JOURNEYS = {};

export class IdentityService {
  static getActiveSessions() {
    try {
      const saved = localStorage.getItem(ACTIVE_SESSIONS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {}
    return INITIAL_USER_SESSIONS;
  }

  static saveActiveSessions(sessions) {
    try {
      localStorage.setItem(ACTIVE_SESSIONS_KEY, JSON.stringify(sessions));
      window.dispatchEvent(new Event('satala_sessions_changed'));
    } catch (e) {}
  }

  static logoutSingleDevice(userId, sessionId) {
    const sessions = this.getActiveSessions();
    let targetUser = null;
    let targetDevice = null;

    const updated = sessions.map(u => {
      if (u.userId === userId) {
        targetUser = u;
        const filteredDevices = u.devices.filter(d => {
          if (d.sessionId === sessionId) {
            targetDevice = d;
            return false;
          }
          return true;
        });
        return { ...u, devices: filteredDevices };
      }
      return u;
    }).filter(u => u.devices.length > 0);

    this.saveActiveSessions(updated);

    if (targetUser && targetDevice) {
      logAuditEvent({
        actor: 'Admin / System Security',
        role: 'Marketing Admin',
        action: 'Logout Single Device',
        target: `${targetUser.userName} (${targetDevice.device} • ${targetDevice.browser})`,
        ip: targetDevice.ip,
        browser: targetDevice.browser,
        device: targetDevice.device,
        country: targetDevice.location,
        result: 'Sukses',
        severity: 'WARNING',
        details: `Terminated session ID ${sessionId} for ${targetUser.email}`
      });
    }
    return updated;
  }

  static forceLogoutAllDevices(userId) {
    const sessions = this.getActiveSessions();
    const targetUser = sessions.find(u => u.userId === userId);
    const updated = sessions.filter(u => u.userId !== userId);
    
    this.saveActiveSessions(updated);

    if (targetUser) {
      logAuditEvent({
        actor: 'Admin Security',
        role: 'Super Admin',
        action: 'Force Logout All Devices',
        target: `${targetUser.userName} (${targetUser.email})`,
        ip: '180.252.120.44',
        browser: 'Chrome 122',
        device: 'Desktop PC',
        country: 'Indonesia',
        result: 'Sukses',
        severity: 'CRITICAL',
        details: `Revoked all active sessions across ${targetUser.devices.length} devices for user ${targetUser.email}`
      });
    }
    return updated;
  }

  static getVisitorJourneys() {
    try {
      const saved = localStorage.getItem(VISITOR_JOURNEY_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed === 'object') return { ...INITIAL_VISITOR_JOURNEY, ...parsed };
      }
    } catch (e) {}
    return INITIAL_VISITOR_JOURNEYS;
  }

  static recordVisitorStep(visitorId, stepInfo) {
    try {
      const journeys = this.getVisitorJourneys();
      const existing = journeys[visitorId] || {
        visitorId,
        name: `Visitor #${visitorId.slice(-4)}`,
        trafficSource: 'Direct Organik',
        device: 'Browser',
        location: 'Indonesia',
        steps: []
      };

      existing.steps.push({
        page: stepInfo.page || 'Browsing Page',
        path: stepInfo.path || window.location.pathname,
        timestamp: new Date().toLocaleTimeString('id-ID'),
        icon: stepInfo.icon || '📄',
        duration: 'Aktif'
      });

      journeys[visitorId] = existing;
      localStorage.setItem(VISITOR_JOURNEY_KEY, JSON.stringify(journeys));
      window.dispatchEvent(new CustomEvent('satala_visitor_journey_updated', { detail: { visitorId, journeys } }));
    } catch (e) {}
  }
}
